import { GridColumnProps } from "@progress/kendo-vue-grid";
import { GridColumnReorderEvent, GridColumnResizeEvent } from "@progress/kendo-vue-grid/dist/es/interfaces/events";
import { SortDescriptor } from "@progress/kendo-data-query";
import eventBus from "@/services/eventBus";
import { deleteUserSetting, loadUserSettings, setUserSetting } from "@/services/userSettings";
import GridLayoutProfile, { emptyGridLayout, GridLayout } from "@/models/GridLayoutProfile";
import { t } from "@/plugins/i18n";

/**
 * Kendo grid mentett elrendezés-profiljai (rmgenuser_setting, type = GridLayout).
 *
 * Bekötés: a page a data()-ban létrehoz egy GridLayoutSettings példányt a grid kulcsával,
 * a grid a `columns` getterre és a `columnReorder` kezelőre köt, a GridLayoutMenu pedig
 * a példányt kapja propként. Részletek: docs/grid-layout-bekotes.md
 */

export const gridLayoutSettingType = "GridLayout";

/** A parancs (gomb) oszlopnak nincs field-je, ezzel a kulccsal kezeljük; mindig a lista végén marad. */
export const commandColumnKey = "command";

export function columnKey(column: GridColumnProps): string {
  return column.field ?? commandColumnKey;
}

export interface GridLayoutSettingsOptions {
  /** A grid egyedi kulcsa (rmgenuser_setting.control_name). Később ne változzon, mert a mentett profilok elvesznek. */
  controlName: string;
  /** A grid alap oszlopdefiníciói (a page computed-je), ebből készül a megjelenített lista. */
  getBaseColumns: () => GridColumnProps[];
  /** A grid aktuális rendezése; a profil ezt menti. */
  getSort: () => SortDescriptor[];
  /** A profil rendezésének visszaállítása a gridre. */
  setSort: (sort: SortDescriptor[]) => void;
  /** Profil betöltése / alap elrendezés után hívódik, ha a rendezés vagy a szűrő változott (pl. szerver oldali újratöltéshez). */
  onSortApplied?: () => void;
  /** A szűrősor aktuális állapota JSON-ba írható alakban; ha megadod, a profil ezt is menti (a `setFilter`-rel együtt). */
  getFilter?: () => unknown;
  /**
   * A profil szűrőjének visszaállítása a gridre; `undefined` = alap elrendezés (üres szűrő).
   * `initial`: az alapértelmezett profil induláskori betöltése (nem a felhasználó választotta).
   */
  setFilter?: (filter: unknown, initial: boolean) => void;
}

function cloneJson<T>(value: T): T {
  return value === undefined ? value : JSON.parse(JSON.stringify(value));
}

function normalizeLayout(value: unknown): GridLayout {
  const raw = (value ?? {}) as Partial<GridLayout>;
  return {
    columnOrder: Array.isArray(raw.columnOrder) ? raw.columnOrder : [],
    hiddenColumns: Array.isArray(raw.hiddenColumns) ? raw.hiddenColumns : [],
    columnWidths: raw.columnWidths && typeof raw.columnWidths === "object" ? raw.columnWidths : {},
    sort: Array.isArray(raw.sort) ? raw.sort : [],
    filter: raw.filter,
  };
}

/** A felhasználó mentett profiljai név szerint rendezve. Hibás JSON-ú sort kihagyjuk. */
export async function loadGridLayoutProfiles(controlName: string): Promise<GridLayoutProfile[]> {
  const settings = await loadUserSettings(controlName, gridLayoutSettingType);
  const profiles: GridLayoutProfile[] = [];

  for (const setting of settings) {
    try {
      profiles.push({
        profileName: setting.profileName,
        isDefault: setting.isDefault,
        layout: normalizeLayout(JSON.parse(setting.userSetting).value),
      });
    } catch (e) {
      console.log(e);
    }
  }

  return profiles.sort((a, b) => a.profileName.localeCompare(b.profileName));
}

export function saveGridLayoutProfile(controlName: string, profile: GridLayoutProfile): Promise<string> {
  return setUserSetting(controlName, gridLayoutSettingType, profile.layout, profile.profileName, profile.isDefault);
}

export function deleteGridLayoutProfile(controlName: string, profileName: string): Promise<string> {
  return deleteUserSetting(controlName, gridLayoutSettingType, profileName);
}

/**
 * Az alap oszlopdefiníciók a mentett sorrendben, a rejtettek nélkül.
 * A sorrendben nem szereplő (pl. később hozzáadott) oszlopok az eredeti helyük szerint a végére kerülnek,
 * a parancs oszlop mindig a legvégén marad.
 */
export function applyGridLayout(baseColumns: GridColumnProps[], layout: GridLayout = emptyGridLayout): GridColumnProps[] {
  const byKey = new Map(baseColumns.map((c) => [columnKey(c), c]));
  const ordered: GridColumnProps[] = [];

  for (const key of layout.columnOrder) {
    const column = byKey.get(key);
    if (column && key !== commandColumnKey) {
      ordered.push(column);
      byKey.delete(key);
    }
  }

  for (const column of baseColumns) {
    const key = columnKey(column);
    if (byKey.has(key) && key !== commandColumnKey) ordered.push(column);
  }

  const command = baseColumns.find((c) => columnKey(c) === commandColumnKey);
  if (command) ordered.push(command);

  return ordered
    .filter((c) => !layout.hiddenColumns.includes(columnKey(c)))
    .map((c, index) => ({ ...c, orderIndex: index, width: layout.columnWidths[columnKey(c)] ?? c.width }));
}

/**
 * Egy grid elrendezés-állapota és profil-műveletei. A page data()-jába téve a mezői reaktívak,
 * ezért a `columns` getter a gridben magától frissül. Az eredményt eventBus "success" / "error" jelzi.
 */
export class GridLayoutSettings {
  profiles: GridLayoutProfile[] = [];
  currentProfileName = "";
  columnOrder: string[] = [];
  hiddenColumns: string[] = [];
  columnWidths: Record<string, number> = {};

  private readonly options: GridLayoutSettingsOptions;

  constructor(options: GridLayoutSettingsOptions) {
    this.options = options;
  }

  get controlName(): string {
    return this.options.controlName;
  }

  /** A grid alap oszlopai (a menü oszlop ki/be listájához). */
  get baseColumns(): GridColumnProps[] {
    return this.options.getBaseColumns();
  }

  /** A gridnek átadandó oszloplista az aktuális elrendezéssel. */
  get columns(): GridColumnProps[] {
    return applyGridLayout(this.baseColumns, {
      columnOrder: this.columnOrder,
      hiddenColumns: this.hiddenColumns,
      columnWidths: this.columnWidths,
      sort: [],
    });
  }

  /**
   * Húzással átrendezett oszlopok: a Kendo által adott orderIndex szerinti sorrendet tároljuk. A Kendo csak a
   * látható oszlopokat adja, ezért a rejtetteket a korábbi helyükön tartjuk meg, hogy visszakapcsolva ne a lista
   * végére, hanem a régi helyükre kerüljenek.
   */
  columnReorder(event: GridColumnReorderEvent): void {
    const visible = [...event.columns].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)).map(columnKey);
    let next = 0;
    this.columnOrder = this.fullColumnOrder().map((key) => (this.hiddenColumns.includes(key) ? key : visible[next++] ?? key));
  }

  /** Az összes alap oszlop kulcsa a jelenlegi sorrendben, a rejtettekkel együtt. */
  private fullColumnOrder(): string[] {
    return applyGridLayout(this.baseColumns, { ...emptyGridLayout, columnOrder: this.columnOrder }).map(columnKey);
  }

  /**
   * Húzással átméretezett oszlop. A kendo-vue-grid 2.1.2 Grid.onResize felcseréli a két paramétert
   * (a ColumnResize `(index, oldWidth, newWidth)` sorrendben hívja), ezért az új szélesség az `oldWidth`-ben jön.
   * Minden lépést alkalmazunk, mert `scrollable="none"` módban a Kendo húzás közben nem rajzol.
   */
  columnResize(event: GridColumnResizeEvent): void {
    const column = event.columns[event.index];
    if (!column) return;
    this.columnWidths = { ...this.columnWidths, [columnKey(column)]: Math.round(event.oldWidth) };
  }

  toggleColumn(field: string): void {
    this.hiddenColumns = this.hiddenColumns.includes(field)
      ? this.hiddenColumns.filter((f) => f !== field)
      : [...this.hiddenColumns, field];
  }

  /** A profilok betöltése; az alapértelmezett rögtön érvénybe lép. Hiba esetén marad az alap elrendezés. */
  async load(): Promise<void> {
    try {
      this.profiles = await loadGridLayoutProfiles(this.controlName);
      const defaultProfile = this.profiles.find((p) => p.isDefault);
      if (defaultProfile) this.apply(defaultProfile, true);
    } catch (e) {
      console.log(e);
    }
  }

  /** Csak az állapotot állítja, újratöltés nélkül (induláskor a page úgyis betölti az adatokat). */
  apply(profile: GridLayoutProfile, initial = false): void {
    this.currentProfileName = profile.profileName;
    this.columnOrder = [...profile.layout.columnOrder];
    this.hiddenColumns = [...profile.layout.hiddenColumns];
    this.columnWidths = { ...profile.layout.columnWidths };
    this.options.setSort([...profile.layout.sort]);
    this.options.setFilter?.(cloneJson(profile.layout.filter), initial);
  }

  /** Menüből választott profil: a rendezés is átáll, ezért a hívó újratölthet. */
  select(profile: GridLayoutProfile): void {
    this.apply(profile);
    this.options.onSortApplied?.();
  }

  reset(): void {
    this.currentProfileName = "";
    this.columnOrder = [];
    this.hiddenColumns = [];
    this.columnWidths = {};
    this.options.setSort([]);
    this.options.setFilter?.(undefined, false);
    this.options.onSortApplied?.();
  }

  private snapshot(profileName: string, isDefault: boolean): GridLayoutProfile {
    return {
      profileName,
      isDefault,
      layout: {
        columnOrder: this.columns.map(columnKey),
        hiddenColumns: [...this.hiddenColumns],
        columnWidths: { ...this.columnWidths },
        sort: [...this.options.getSort()],
        filter: cloneJson(this.options.getFilter?.()),
      },
    };
  }

  async saveAs(profileName: string, isDefault: boolean, successMessage?: string): Promise<void> {
    try {
      const response = await saveGridLayoutProfile(this.controlName, this.snapshot(profileName, isDefault));
      if (response !== "success") {
        eventBus.$emit("error", t("AzElrendezesMenteseNemSikerult"));
        return;
      }

      this.currentProfileName = profileName;
      this.profiles = await loadGridLayoutProfiles(this.controlName);
      eventBus.$emit("success", successMessage ?? t("ElrendezesMentve", [profileName]));
    } catch (e) {
      console.log(e);
      eventBus.$emit("error", t("AzElrendezesMenteseNemSikerult"));
    }
  }

  /** A betöltött profil felülírása az aktuális állapottal. */
  async saveCurrent(): Promise<void> {
    const existing = this.profiles.find((p) => p.profileName === this.currentProfileName);
    if (!existing) return;

    await this.saveAs(existing.profileName, existing.isDefault, t("ElrendezesFelulirva", [existing.profileName]));
  }

  /** A profil tartalma marad, csak az alapértelmezett jel kerül át rá (a szerver a többiről leveszi). */
  async setDefault(profile: GridLayoutProfile): Promise<void> {
    if (profile.isDefault) return;

    try {
      const response = await saveGridLayoutProfile(this.controlName, { ...profile, isDefault: true });
      if (response !== "success") {
        eventBus.$emit("error", t("AzAlapertelmezettElrendezesBeallitasaNemSikerult"));
        return;
      }

      this.profiles = await loadGridLayoutProfiles(this.controlName);
      eventBus.$emit("success", t("LettAzAlapertelmezettElrendezes", [profile.profileName]));
    } catch (e) {
      console.log(e);
      eventBus.$emit("error", t("AzAlapertelmezettElrendezesBeallitasaNemSikerult"));
    }
  }

  async delete(profileName: string): Promise<void> {
    try {
      const response = await deleteGridLayoutProfile(this.controlName, profileName);
      if (response !== "success") {
        eventBus.$emit("error", t("AzElrendezesTorleseNemSikerult"));
        return;
      }

      if (this.currentProfileName === profileName) this.currentProfileName = "";
      this.profiles = await loadGridLayoutProfiles(this.controlName);
      eventBus.$emit("success", t("ElrendezesTorolve", [profileName]));
    } catch (e) {
      console.log(e);
      eventBus.$emit("error", t("AzElrendezesTorleseNemSikerult"));
    }
  }
}
